import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { NeedsAccount } from './AccountInfo';
import { ArrowLeft, BookOpen, ChevronRight, ListChecks, NotebookPen } from 'lucide-react';
import { useI18n } from '../i18n';
import { getMeta, setMeta } from '../db/db';
import { Switch } from '../ui/Field';
import { clearConversation, conversationOwner, loadConversation, loadPreferences, savePreferences, TTL_MS } from '../assistant/store';
import { describePreference, emptyPreferences, type PreferenceChange, type Preferences } from '@habitos/assistant/assistant/preferences';
import { useToast } from '../ui/Toast';
import { fetchAiStatus, type AiStatus } from '../assistant/remote';
import { api } from '../sync/api';
import { useSyncState } from '../sync/store';

type Availability = 'available' | 'setup' | 'unavailable';

/** Integraciones con su estado real: nada se presenta como activo si no funciona. */
const INTEGRATIONS: { key: string; status: Availability }[] = [{ key: 'googleCalendar', status: 'unavailable' }];

/**
 * Ajustes del asistente y del Cerebro: proveedor de IA, memoria de la conversación,
 * privacidad, accesos al Cerebro (notas, listas y diario) e integraciones.
 */
export function AssistantSettingsScreen() {
  const { t, fmtDate } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const [brief, setBrief] = useState(true);
  const [useAi, setUseAi] = useState(true);
  const [ai, setAi] = useState<AiStatus | null | undefined>(undefined);
  const [conv, setConv] = useState<{
    owner: string;
    messages: number;
    updatedAt: number | null;
  } | null>(null);

  useEffect(() => {
    let alive = true;
    void getMeta<boolean>('assistantBrief', true).then((v) => alive && setBrief(v));
    void getMeta<boolean>('assistantUseAi', true).then((v) => alive && setUseAi(v));
    void fetchAiStatus().then((v) => alive && setAi(v));
    void (async () => {
      const owner = await conversationOwner();
      const s = await loadConversation(owner, Date.now());
      if (alive)
        setConv({
          owner,
          messages: s?.messages.length ?? 0,
          updatedAt: s?.updatedAt ?? null,
        });
    })();
    return () => {
      alive = false;
    };
  }, []);

  const clear = async () => {
    if (!conv) return;
    await clearConversation(conv.owner);
    setConv({ ...conv, messages: 0, updatedAt: null });
    toast.show(t('assistantSettings.memory.cleared'));
  };

  return (
    <div className="stack-l">
      <header className="row">
        <button type="button" className="icon-btn" onClick={() => navigate('/settings')} aria-label={t('common.back')}>
          <ArrowLeft size={22} aria-hidden="true" />
        </button>
        <h1>{t('assistantSettings.title')}</h1>
      </header>

      <section className="form-section" aria-labelledby="as-ai">
        <h2 id="as-ai">{t('assistantSettings.ai.title')}</h2>
        <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
          <span>
            {ai?.provider && ai.provider !== 'none' ? t(`assistantSettings.ai.providers.${ai.provider}`) : t('assistantSettings.ai.rules')}
            {ai?.model ? <span className="muted"> · {ai.model}</span> : null}
          </span>
          <span className={`badge${ai?.available ? ' badge--completed' : ''}`} data-testid="ai-status" role="status">
            {ai === undefined ? t('assistantSettings.ai.checking') : ai?.available ? t('assistantSettings.ai.connected') : t('assistantSettings.ai.notConnected')}
          </span>
        </div>
        {ai !== undefined && <p className="small">{t(`assistantSettings.ai.detail.${ai ? ai.detail : 'offline'}`)}</p>}
        <p className="muted small">{t('assistantSettings.ai.rulesHelp')}</p>
        {ai?.available && (
          <Switch
            label={t('assistantSettings.ai.use')}
            help={t(ai.provider === 'ollama' ? 'assistantSettings.ai.useHelpLocal' : 'assistantSettings.ai.useHelpRemote')}
            checked={useAi}
            onChange={(v) => {
              setUseAi(v);
              void setMeta('assistantUseAi', v);
            }}
          />
        )}
        {!ai?.available && (
          <details className="small">
            <summary>{t('assistantSettings.ai.howTo')}</summary>
            <ol className="muted stack" style={{ paddingLeft: '1.2rem' }}>
              <li>{t('assistantSettings.ai.step1')}</li>
              <li>{t('assistantSettings.ai.step2')}</li>
              <li>{t('assistantSettings.ai.step3')}</li>
            </ol>
          </details>
        )}
      </section>

      <section className="form-section" aria-labelledby="as-prefs">
        <h2 id="as-prefs">{t('assistantSettings.prefs.title')}</h2>
        <Switch
          label={t('assistantSettings.prefs.brief')}
          help={t('assistantSettings.prefs.briefHelp')}
          checked={brief}
          onChange={(v) => {
            setBrief(v);
            void setMeta('assistantBrief', v);
          }}
        />
      </section>

      <section className="form-section" aria-labelledby="as-memory">
        <h2 id="as-memory">{t('assistantSettings.memory.title')}</h2>
        <p className="muted small">{t('assistantSettings.memory.body', { hours: TTL_MS / 3_600_000 })}</p>
        {conv && (
          <p className="small" data-testid="conversation-size">
            {conv.messages > 0 && conv.updatedAt
              ? t('assistantSettings.memory.current', {
                  count: conv.messages,
                  date: fmtDate(new Date(conv.updatedAt).toISOString().slice(0, 10), { day: 'numeric', month: 'long' }),
                })
              : t('assistantSettings.memory.empty')}
          </p>
        )}
        <div>
          <button type="button" className="btn btn--outline" onClick={() => void clear()} disabled={!conv || conv.messages === 0}>
            {t('assistantSettings.memory.clear')}
          </button>
        </div>
      </section>

      <PreferencesSection />

      <section className="form-section" aria-labelledby="as-privacy">
        <h2 id="as-privacy">{t('assistantSettings.privacy.title')}</h2>
        <ul className="muted small stack" style={{ paddingLeft: '1.1rem', margin: 0 }}>
          <li>{t(ai?.available && useAi ? (ai.provider === 'ollama' ? 'assistantSettings.privacy.aiLocal' : 'assistantSettings.privacy.aiRemote') : 'assistantSettings.privacy.local')}</li>
          <li>{t('assistantSettings.privacy.confirm')}</li>
          <li>{t('assistantSettings.privacy.journal')}</li>
        </ul>
      </section>

      <section className="form-section" aria-labelledby="as-brain">
        <h2 id="as-brain">{t('assistantSettings.brain.title')}</h2>
        <p className="muted small">{t('assistantSettings.brain.body')}</p>
        <nav className="list-card" aria-labelledby="as-brain">
          <Link to="/brain?tab=notes" className="list-item">
            <NotebookPen size={20} aria-hidden="true" />
            <span className="list-item__body list-item__title">{t('brain.tabs.notes')}</span>
            <ChevronRight size={18} aria-hidden="true" />
          </Link>
          <Link to="/brain?tab=lists" className="list-item">
            <ListChecks size={20} aria-hidden="true" />
            <span className="list-item__body list-item__title">{t('brain.tabs.lists')}</span>
            <ChevronRight size={18} aria-hidden="true" />
          </Link>
          <Link to="/brain?tab=journal" className="list-item">
            <BookOpen size={20} aria-hidden="true" />
            <span className="list-item__body list-item__title">{t('brain.tabs.journal')}</span>
            <ChevronRight size={18} aria-hidden="true" />
          </Link>
        </nav>
      </section>

      <section className="form-section" aria-labelledby="as-integrations">
        <h2 id="as-integrations">{t('assistantSettings.integrations.title')}</h2>
        <WhatsAppCard />
        <ul className="list-card">
          {INTEGRATIONS.map((i) => (
            <li key={i.key} className="list-item">
              <span className="list-item__body">
                <span className="list-item__title">{t(`assistantSettings.integrations.${i.key}.name`)}</span>
                <span className="list-item__sub">{t(`assistantSettings.integrations.${i.key}.body`)}</span>
              </span>
              <span className={`badge${i.status === 'available' ? ' badge--completed' : ''}`}>{t(`assistantSettings.status.${i.status}`)}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

interface WhatsAppStatus {
  configured: boolean;
  number: string | null;
  linked: { address: string } | null;
}

/**
 * WhatsApp con su estado real: necesita cuenta (el servidor lee los datos sincronizados) y que
 * el servidor tenga configurada la API de WhatsApp Cloud. La vinculación es con un código.
 */
function WhatsAppCard() {
  const { t, tag } = useI18n();
  const sync = useSyncState();
  const toast = useToast();
  const [status, setStatus] = useState<WhatsAppStatus | null | undefined>(undefined);
  const [code, setCode] = useState<{ code: string; expiresAt: string } | null>(null);

  useEffect(() => {
    if (!sync.signedIn) return;
    let alive = true;
    api<WhatsAppStatus>('/channels/whatsapp')
      .then((s) => alive && setStatus(s))
      .catch(() => alive && setStatus(null));
    return () => {
      alive = false;
    };
  }, [sync.signedIn]);

  const state: Availability = status?.linked ? 'available' : status?.configured ? 'setup' : 'unavailable';
  return (
    <section className="card stack" aria-labelledby="wa-title" data-testid="whatsapp-card">
      <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <h3 id="wa-title" style={{ margin: 0 }}>
          WhatsApp
        </h3>
        <span className={`badge${state === 'available' ? ' badge--completed' : ''}`} role="status">
          {sync.signedIn && status !== undefined ? t(`assistantSettings.status.${state}`) : t('assistantSettings.status.unavailable')}
        </span>
      </div>
      <p className="muted small">{t('assistantSettings.whatsapp.what')}</p>
      {!sync.signedIn ? (
        <NeedsAccount feature="whatsapp" />
      ) : status === undefined ? null : status === null ? (
        <p className="small">{t('assistantSettings.ai.detail.offline')}</p>
      ) : !status.configured ? (
        <p className="small">{t('assistantSettings.whatsapp.notConfigured')}</p>
      ) : status.linked ? (
        <>
          <p className="small">{t('assistantSettings.whatsapp.linked', { number: status.linked.address })}</p>
          <div>
            <button
              type="button"
              className="btn btn--outline btn--sm"
              onClick={() =>
                void api('/channels/whatsapp', { method: 'DELETE' })
                  .then(() => setStatus({ ...status, linked: null }))
                  .catch(() => toast.show(t('sync.errors.generic')))
              }
            >
              {t('assistantSettings.whatsapp.unlink')}
            </button>
          </div>
        </>
      ) : code ? (
        <div className="stack">
          <p className="small">{t('assistantSettings.whatsapp.sendCode', { number: status.number ?? t('assistantSettings.whatsapp.theNumber') })}</p>
          <p className="wa-code num" data-testid="wa-code">
            vincular {code.code}
          </p>
          <p className="muted xsmall">{t('assistantSettings.whatsapp.expires', { time: new Date(code.expiresAt).toLocaleTimeString(tag, { hour: '2-digit', minute: '2-digit' }) })}</p>
        </div>
      ) : (
        <div>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() =>
              void api<{ code: string; expiresAt: string }>('/channels/whatsapp/code', { method: 'POST' })
                .then(setCode)
                .catch(() => toast.show(t('sync.errors.generic')))
            }
          >
            {t('assistantSettings.whatsapp.getCode')}
          </button>
        </div>
      )}
      <p className="muted xsmall">{t('assistantSettings.whatsapp.privacy')}</p>
    </section>
  );
}

/** Lo que el asistente recuerda de la persona: visible y borrable, una a una o todo. */
function PreferencesSection() {
  const { t } = useI18n();
  const toast = useToast();
  const [state, setState] = useState<{ owner: string; prefs: Preferences } | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      const owner = await conversationOwner();
      const prefs = await loadPreferences(owner);
      if (alive) setState({ owner, prefs });
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (!state) return null;
  const { owner, prefs } = state;
  const items: { key: string; change: PreferenceChange; forget: (p: Preferences) => Preferences }[] = [];
  if (prefs.earliest) items.push({ key: 'earliest', change: { kind: 'earliest', time: prefs.earliest }, forget: (p) => ({ ...p, earliest: null }) });
  if (prefs.latest) items.push({ key: 'latest', change: { kind: 'latest', time: prefs.latest }, forget: (p) => ({ ...p, latest: null }) });
  for (const [k, v] of Object.entries(prefs.slots)) {
    items.push({
      key: `slot-${k}`,
      change: { kind: 'slot', activity: v.label, slot: v.slot },
      forget: (p) => ({ ...p, slots: Object.fromEntries(Object.entries(p.slots).filter(([x]) => x !== k)) }),
    });
  }
  if (prefs.freeDays.length) items.push({ key: 'freeDays', change: { kind: 'freeDays', days: prefs.freeDays }, forget: (p) => ({ ...p, freeDays: [] }) });

  const save = async (next: Preferences) => {
    await savePreferences(owner, next);
    setState({ owner, prefs: next });
    toast.show(t('assistantSettings.prefsMemory.forgotten'));
  };

  return (
    <section className="form-section" aria-labelledby="as-prefs-memory">
      <h2 id="as-prefs-memory">{t('assistantSettings.prefsMemory.title')}</h2>
      <p className="muted small">{t('assistantSettings.prefsMemory.body')}</p>
      {items.length === 0 ? (
        <p className="small">{t('chat.prefs.howTo')}</p>
      ) : (
        <>
          <ul className="list-card">
            {items.map((i) => (
              <li key={i.key} className="list-item">
                <span className="list-item__body list-item__title">{describePreference(i.change, t)}</span>
                <button type="button" className="btn btn--ghost btn--sm" onClick={() => void save(i.forget(prefs))}>
                  {t('assistantSettings.prefsMemory.forget')}
                </button>
              </li>
            ))}
          </ul>
          <div>
            <button type="button" className="btn btn--outline btn--sm" onClick={() => void save(emptyPreferences())}>
              {t('assistantSettings.prefsMemory.forgetAll')}
            </button>
          </div>
        </>
      )}
    </section>
  );
}
